import type { ModuleInfo } from '../Bridge';
import { Bridge } from '../Bridge';
import { once } from '../utils';
import type { Version } from '../Version';

/** The attribution details for an app download. */
export type Attribution =
    | {
          /** The attribution value. A value of true returns if a user clicks an Apple Search Ads impression up to 30 days before your app download. If the API can’t find a matching attribution record, the attribution value is false. */
          attribution: false;
      }
    | {
          /** The attribution value. A value of true returns if a user clicks an Apple Search Ads impression up to 30 days before your app download. If the API can’t find a matching attribution record, the attribution value is false. */
          attribution: true;
          /** The identifier of the organization that owns the campaign. Your orgId is the same as your account in the Apple Search Ads UI. */
          orgId: number;
          /** The unique identifier for the campaign. */
          campaignId: number;
          /** The type of conversion is either `Download` or `Redownload`. */
          conversionType: 'Download' | 'Redownload';
          /** The date and time when the user clicks an ad in a corresponding campaign. This field only appears in the detailed attribution response payload. */
          clickDate?: string;
          /** The identifier for the ad group. */
          adGroupId: number;
          /** The country or region for the campaign. */
          countryOrRegion: string;
          /** The identifier for the keyword. */
          keywordId: number;
          /** The identifier representing the assignment relationship between an ad object and an ad group. */
          adId: number;
      };

export type FetchAttributionResponse = Attribution | null;

export class AppleSearchAdsModule {
    constructor(
        readonly bridge: Bridge,
        readonly module: ModuleInfo<'apple-search-ads', 0>,
    ) {}

    private request<T>(data: any): Promise<T> {
        return this.bridge.request(this.module.id, data);
    }

    /**
     * Since apple-search-ads@0.0.0
     */
    async fetch(): Promise<FetchAttributionResponse> {
        return this.request({
            type: 'fetch',
        });
    }
}

/**
 * The Apple Search Ads module provides a method to check attribution details for the current app download.
 *
 * Get an instance of this module using `AppleSearchAds.getInstance()`.
 */
export class AppleSearchAds {
    /**
     * Creates a new instance using the provided bridge.
     */
    static create = (bridge: Bridge) => {
        const module = bridge.findModule('apple-search-ads', 0);
        if (module) {
            return new AppleSearchAds(new AppleSearchAdsModule(bridge, module), module.version);
        }
        return undefined;
    };

    /**
     * Returns a shared instance.
     */
    static getInstance = once(() => {
        const bridge = Bridge.getInstance();
        if (bridge) {
            return AppleSearchAds.create(bridge);
        }
        return undefined;
    });

    /** Fetches the attribution details for the current app download. */
    readonly fetch: () => Promise<FetchAttributionResponse>;

    constructor(
        readonly module: AppleSearchAdsModule,
        readonly version: Version,
    ) {
        this.fetch = module.fetch.bind(module);
    }
}
